import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useSite } from '../../context/SiteContext';
import { toast } from 'react-hot-toast';
import { EMPTY_USER_FILTERS, filterUsers } from './userFilters';

const allRoles = ['admin_master', 'admin_site', 'admin_analytics', 'analyst', 'operator', 'operator_conversion', 'operator_production', 'supervisor_conversion'];
// admin_site no puede asignar admin_master
const rolesForAdminSite = ['admin_site', 'admin_analytics', 'analyst', 'operator', 'operator_conversion', 'operator_production', 'supervisor_conversion'];

const getApiError = (error, fallback) => {
  const validationErrors = error.response?.data?.errors;
  const firstValidationError = validationErrors
    ? Object.values(validationErrors).flat().find(Boolean)
    : null;

  return error.response?.data?.detail
    || firstValidationError
    || error.response?.data?.message
    || fallback;
};

const Users = () => {
  const { isAuthenticated, loading: authLoading, isAdminMaster, isAdminSite, isAdmin } = useAuth();
  const { t } = useLanguage();
  const { sites, currentSite, getCurrentSiteId } = useSite();
  const navigate = useNavigate();

  // Roles disponibles según el tipo de admin
  const availableRoles = isAdminMaster() ? allRoles : rolesForAdminSite;

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_USER_FILTERS);

  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ 
    username: '',
    email: '',
    password: '', 
    role: 'operator', 
    active: true,
    site_id: null 
  });

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ 
    username: '',
    email: '',
    password: '', 
    role: 'operator', 
    active: true,
    site_id: null 
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const filteredUsers = useMemo(
    () => filterUsers(users, filters, sites),
    [users, filters, sites]
  );
  const filterRoleOptions = useMemo(
    () => [...new Set(users.map((user) => user.role).filter(Boolean))].sort(),
    [users]
  );
  const hasActiveFilters = Object.values(filters).some((value) => value !== '' && value !== 'all');

  const updateFilter = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setOpenMenuId(null);
  };

  // Helper function to get headers
  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Esta sección administra exclusivamente usuarios de sistema con email.
      const siteId = getCurrentSiteId();
      const params = { per_page: 'all', user_type: 'system_user' };
      if (siteId) {
        params.site_id = siteId;
      }
      
      const { data } = await axios.get('/users/', { headers: getHeaders(), params });
      // Backend retorna paginado, extraer el array de datos
      const usersList = Array.isArray(data) ? data : (data.data || []);
      setUsers(usersList);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setError(detail);
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate, getCurrentSiteId]);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated()) return navigate('/login');
      if (!isAdmin()) {
        setError(t('users.messages.adminOnlyError'));
        setLoading(false);
        return;
      }
      loadUsers();
    }
  }, [authLoading, isAdmin, isAuthenticated, loadUsers, navigate, t]);

  // Recargar usuarios cuando cambia el sitio
  useEffect(() => {
    if (currentSite && !authLoading && isAuthenticated() && isAdmin()) {
      loadUsers();
    }
  }, [currentSite, authLoading, isAuthenticated, isAdmin, loadUsers]);

  // Inicializar site_id al cambiar el sitio actual
  useEffect(() => {
    if (currentSite && isAdminSite()) {
      // Para admin_site, siempre usar su sitio
      setNewUser(prev => ({ ...prev, site_id: currentSite.id }));
    } else if (currentSite && !newUser.site_id) {
      // Para admin_master, sugerir el sitio actual por defecto
      setNewUser(prev => ({ ...prev, site_id: currentSite.id }));
    }
  }, [currentSite, isAdminSite, newUser.site_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.password) {
      return toast.error(t('users.messages.requiredFields'));
    }
    try {
      setCreating(true);
      const payload = {
        password: newUser.password,
        role: newUser.role,
        active: newUser.active,
        site_id: newUser.site_id,
        email: newUser.email.trim(),
        username: newUser.username.trim()
      };
      
      // Asegurar que se use el site_id del sitio actual si no está establecido
      if (!payload.site_id) {
        payload.site_id = getCurrentSiteId();
      }
      
      // Si es admin_site, forzar su site_id
      if (isAdminSite() && currentSite) {
        payload.site_id = currentSite.id;
      }
      
      const { data } = await axios.post('/users/', payload, { headers: getHeaders() });
      setUsers((prev) => [data, ...prev]);
      setNewUser({ username: '', email: '', password: '', role: 'operator', active: true, site_id: currentSite?.id || null });
      toast.success(t('users.messages.createSuccess'));
    } catch (e) {
      toast.error(getApiError(e, t('users.messages.createError')));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    // admin_site no puede editar usuarios admin_master
    if (isAdminSite() && u.role === 'admin_master') {
      toast.error(t('users.messages.cannotEditMaster'));
      return;
    }
    
    setEditingId(u.id);
    setEditData({ 
      username: u.username,
      email: u.email || '',
      password: '', 
      role: u.role, 
      active: u.active,
      site_id: u.site_id 
    });
    setOpenMenuId(null); // Cerrar el menú al empezar a editar
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (id) => {
    if (!editData.username.trim() || !editData.email.trim()) {
      return toast.error(t('users.messages.requiredFields'));
    }

    try {
      const payload = { 
        username: editData.username.trim(),
        email: editData.email.trim(),
        role: editData.role, 
        active: editData.active,
        site_id: editData.site_id
      };
      
      // Si es admin_site, no permitir cambiar el site_id
      if (isAdminSite() && currentSite) {
        payload.site_id = currentSite.id;
      }
      
      if (editData.password) payload.password = editData.password;
      const { data } = await axios.put(`/users/${id}`, payload, { headers: getHeaders() });
      setUsers((prev) => prev.map((u) => (u.id === id ? data : u)));
      setEditingId(null);
      toast.success(t('users.messages.updateSuccess'));
    } catch (e) {
      toast.error(getApiError(e, t('users.messages.updateError')));
    }
  };

  const toggleActive = async (id) => {
    try {
      // admin_site no puede activar/desactivar usuarios admin_master
      const user = users.find(u => u.id === id);
      if (isAdminSite() && user?.role === 'admin_master') {
        toast.error(t('users.messages.cannotModifyMaster'));
        return;
      }
      
      const { data } = await axios.put(`/users/${id}/status`, {}, { headers: getHeaders() });
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, active: data.active } : u)));
      setOpenMenuId(null); // Cerrar el menú después de la acción
    } catch (e) {
      toast.error(e.response?.data?.detail || t('users.messages.statusError'));
    }
  };

  const removeUser = async (id) => {
    // admin_site no puede eliminar usuarios admin_master
    const user = users.find(u => u.id === id);
    if (isAdminSite() && user?.role === 'admin_master') {
      toast.error(t('users.messages.cannotDeleteMaster'));
      return;
    }
    
    if (!window.confirm(t('users.messages.confirmDelete'))) return;
    try {
      await axios.delete(`/users/${id}`, { headers: getHeaders() });
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success(t('users.messages.deleteSuccess'));
      setOpenMenuId(null); // Cerrar el menú después de la acción
    } catch (e) {
      toast.error(e.response?.data?.detail || t('users.messages.deleteError'));
    }
  };

  if (loading) return <div className="flex justify-center items-center h-52 text-blue-700 font-semibold">{t('users.loading')}</div>;
  if (error) return <div className="bg-red-100 border border-red-400 text-red-800 rounded-xl p-6 max-w-xl mx-auto mt-8 shadow-lg">{error}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-blue-900">{t('users.title')}</h1>
        {currentSite && (
          <div className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg font-semibold border-2 border-blue-300">
            📍 {currentSite.code} - {currentSite.name}
          </div>
        )}
      </div>
      <p className="text-gray-700 mb-6">{t('users.subtitle')} <span className="font-semibold text-blue-700">{t('users.adminOnly')}</span></p>

      <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-800 mb-4">{t('users.createTitle')}</h2>
        <form className="flex flex-wrap gap-4 items-end" onSubmit={handleCreate} autoComplete="off">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.username')}</label>
            <input 
              className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" 
              value={newUser.username} 
              onChange={(e) => setNewUser((p) => ({ ...p, username: e.target.value }))}
              placeholder={t('users.form.namePlaceholder')}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.email')}</label>
            <input
              type="email"
              className="w-48 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400"
              value={newUser.email}
              onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
              placeholder={t('users.form.emailPlaceholder')}
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.password')}</label>
            <input 
              type="password" 
              className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" 
              value={newUser.password} 
              onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.role')}</label>
            <select className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" value={newUser.role} onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}>
              {availableRoles.map((r) => (
                <option key={r} value={r}>{r === 'supervisor_conversion' ? t('users.roles.supervisor_conversion') : r}</option>
              ))}
            </select>
          </div>
          {/* Selector de sitio: solo visible para admin_master, fijo para admin_site */}
          {isAdminMaster() ? (
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.site')}</label>
              <select 
                className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-900 focus:ring-2 focus:ring-blue-400" 
                value={newUser.site_id || ''} 
                onChange={(e) => setNewUser((p) => ({ ...p, site_id: parseInt(e.target.value) }))}
              >
                <option value="">{t('users.form.selectSite')}</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.code} - {site.name}</option>
                ))}
              </select>
            </div>
          ) : isAdminSite() && currentSite ? (
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">{t('users.form.site')}</label>
              <input 
                type="text" 
                className="w-40 rounded border border-gray-300 px-3 py-2 text-gray-700 bg-gray-100" 
                value={`${currentSite.code} - ${currentSite.name}`}
                disabled 
              />
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <input id="new-active" className="rounded border-gray-300 accent-blue-600" type="checkbox" checked={newUser.active} onChange={(e) => setNewUser((p) => ({ ...p, active: e.target.checked }))} />
            <label className="text-sm font-semibold text-gray-800" htmlFor="new-active">{t('users.form.active')}</label>
          </div>
          <div>
            <button className="px-4 py-2 bg-blue-700 text-white font-bold rounded-lg shadow hover:bg-blue-800 transition disabled:opacity-60" disabled={creating} type="submit">{creating ? t('users.buttons.creating') : t('users.buttons.create')}</button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-6 border border-blue-100">
        <h2 className="text-xl font-bold text-blue-800 mb-4">{t('users.listTitle')}</h2>
        <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className={isAdminMaster() ? 'lg:col-span-1' : 'lg:col-span-2'}>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-600" htmlFor="users-search">
                {t('users.filters.search')}
              </label>
              <input
                id="users-search"
                type="search"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
                placeholder={t('users.filters.searchPlaceholder')}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-600" htmlFor="users-role-filter">
                {t('users.filters.role')}
              </label>
              <select
                id="users-role-filter"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                value={filters.role}
                onChange={(event) => updateFilter('role', event.target.value)}
              >
                <option value="all">{t('users.filters.allRoles')}</option>
                {filterRoleOptions.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-600" htmlFor="users-status-filter">
                {t('users.filters.status')}
              </label>
              <select
                id="users-status-filter"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value="all">{t('users.filters.allStatuses')}</option>
                <option value="active">{t('users.filters.active')}</option>
                <option value="inactive">{t('users.filters.inactive')}</option>
              </select>
            </div>
            {isAdminMaster() && (
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-600" htmlFor="users-site-filter">
                  {t('users.filters.site')}
                </label>
                <select
                  id="users-site-filter"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                  value={filters.siteId}
                  onChange={(event) => updateFilter('siteId', event.target.value)}
                >
                  <option value="all">{t('users.filters.allSites')}</option>
                  {sites.map((site) => (
                    <option key={site.id} value={site.id}>{site.code} - {site.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
            <span>{t('users.filters.results').replace('{shown}', filteredUsers.length).replace('{total}', users.length)}</span>
            {hasActiveFilters && (
              <button
                type="button"
                className="font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                onClick={() => {
                  setFilters(EMPTY_USER_FILTERS);
                  setOpenMenuId(null);
                }}
              >
                {t('users.filters.clear')}
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 rounded-xl">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                <th className="px-4 py-2 font-bold">{t('users.table.username')}</th>
                <th className="px-4 py-2 font-bold">{t('users.table.email')}</th>
                <th className="px-4 py-2 font-bold">{t('users.table.role')}</th>
                <th className="px-3 py-2 font-bold text-center text-xs">{t('users.table.active')}</th>
                <th className="px-3 py-2 font-bold text-xs">{t('users.table.created')}</th>
                <th className="px-4 py-2 font-bold">{t('users.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const userSite = sites.find(s => s.id === u.site_id);
                return (
                <tr key={u.id} className="even:bg-gray-50">
                  <td className="px-4 py-2">
                    {editingId === u.id ? (
                      <input 
                        className="w-full min-w-[180px] rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400" 
                        value={editData.username} 
                        onChange={(e) => setEditData((p) => ({ ...p, username: e.target.value }))}
                        placeholder={t('users.form.namePlaceholder')}
                        autoComplete="off"
                      />
                    ) : (
                      <div className="font-medium text-gray-900">
                        {u.username}
                        {userSite && (
                          <span className="ml-2 text-xs text-gray-500">({userSite.code})</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === u.id ? (
                      <input
                        type="email"
                        className="w-full min-w-[200px] rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400"
                        value={editData.email}
                        onChange={(e) => setEditData((p) => ({ ...p, email: e.target.value }))}
                        placeholder={t('users.form.emailPlaceholder')}
                        autoComplete="off"
                      />
                    ) : (
                      <div className="text-sm text-gray-700">{u.email || '-'}</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {editingId === u.id ? (
                      <select className="w-full min-w-[140px] rounded border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:ring-2 focus:ring-blue-400" value={editData.role} onChange={(e) => setEditData((p) => ({ ...p, role: e.target.value }))}>
                        {availableRoles.map((r) => (
                          <option key={r} value={r}>{r === 'supervisor_conversion' ? t('users.roles.supervisor_conversion') : r}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm font-semibold text-gray-700">
                        {u.role === 'supervisor_conversion' ? t('users.roles.supervisor_conversion') : u.role}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className={`w-2.5 h-2.5 rounded-full mx-auto ${u.active ? 'bg-green-500' : 'bg-gray-400'}`} title={u.active ? t('users.tooltips.active') : t('users.tooltips.inactive')}></div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{u.created_at ? String(u.created_at).slice(5, 10) : '-'}</td>
                  <td className="px-4 py-2">
                    {editingId === u.id ? (
                      <div className="flex flex-col gap-1">
                        <input 
                          type="password" 
                          placeholder={t('users.form.newPassword')} 
                          className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:ring-2 focus:ring-blue-400" 
                          value={editData.password} 
                          onChange={(e) => setEditData((p) => ({ ...p, password: e.target.value }))}
                          autoComplete="new-password"
                        />
                        {isAdminMaster() && (
                          <select 
                            className="w-full rounded border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:ring-2 focus:ring-blue-400" 
                            value={editData.site_id || ''} 
                            onChange={(e) => setEditData((p) => ({ ...p, site_id: parseInt(e.target.value) }))}
                            title={t('users.form.site')}
                          >
                            <option value="">{t('users.form.selectSite')}</option>
                            {sites.map((site) => (
                              <option key={site.id} value={site.id}>{site.code} - {site.name}</option>
                            ))}
                          </select>
                        )}
                        <div className="flex gap-1">
                          <button className="flex-1 px-2 py-1 bg-green-600 text-white text-xs font-bold rounded shadow hover:bg-green-700 transition" onClick={() => saveEdit(u.id)}>💾 {t('users.buttons.save')}</button>
                          <button className="flex-1 px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded hover:bg-gray-300 transition" onClick={cancelEdit}>✕ {t('users.buttons.cancel')}</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <button 
                          className={`px-3 py-1.5 text-lg font-bold rounded hover:bg-gray-100 transition ${
                            isAdminSite() && u.role === 'admin_master' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600'
                          }`}
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMenuPosition({ x: rect.right, y: rect.bottom });
                            setOpenMenuId(openMenuId === u.id ? null : u.id);
                          }}
                          disabled={isAdminSite() && u.role === 'admin_master'}
                          title={t('users.tooltips.actions')}
                        >
                          ⋮
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
              })}
              {filteredUsers.length === 0 && (
                <tr>
                  <td className="px-4 py-8 text-center text-gray-500" colSpan="6">
                    {t('users.filters.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Floating dropdown menu */}
        {openMenuId && (
          <>
            <div
              className="fixed inset-0 z-20"
              onClick={() => setOpenMenuId(null)}
            />
            <div
              className="fixed z-30 bg-white border border-gray-300 rounded-lg shadow-2xl min-w-[160px] py-1"
              style={{
                left: `${menuPosition.x}px`,
                top: `${menuPosition.y}px`,
                transform: 'translateX(-100%)'
              }}
            >
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-blue-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  startEdit(users.find(u => u.id === openMenuId));
                  setOpenMenuId(null);
                }}
              >
                ✏️ {t('users.buttons.edit')}
              </button>
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-yellow-50 text-yellow-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  toggleActive(openMenuId);
                  setOpenMenuId(null);
                }}
              >
                {users.find(u => u.id === openMenuId)?.active
                  ? `🔒 ${t('users.buttons.deactivate')}`
                  : `🔓 ${t('users.buttons.activate')}`}
              </button>
              <hr className="my-1 border-gray-200" />
              <button
                className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-700 font-semibold flex items-center gap-2"
                onClick={() => {
                  removeUser(openMenuId);
                  setOpenMenuId(null);
                }}
              >
                🗑️ {t('users.buttons.delete')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
